<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Trial Balance
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <!-- Date Filters -->
                <v-row class="py-2 pl-2">
                  <v-col lg="3" md="4" cols="12">
                    <v-date-input 
                      input-format="dd/mm/yyyy"
                      v-model="filters.from_date" 
                      label="From Date" 
                      density="compact" 
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-start" 
                      variant="outlined" 
                      bg-color="grey-lighten-4" 
                      autocomplete="off"
                      clearable
                    />
                  </v-col>
                  <v-col lg="3" md="4" cols="12">
                    <v-date-input 
                      input-format="dd/mm/yyyy"
                      v-model="filters.to_date" 
                      label="To Date" 
                      density="compact" 
                      prepend-icon=""
                      prepend-inner-icon="mdi-calendar-end" 
                      variant="outlined" 
                      bg-color="grey-lighten-4" 
                      autocomplete="off"
                      clearable
                    />
                  </v-col>
                  <v-col lg="3" md="4" cols="12">
                    <v-autocomplete
                      v-model="filters.acc_account_head_id"
                      :items="accountHeadOptions"
                      item-title="text"
                      item-value="value"
                      label="All Account Head"
                      variant="outlined"
                      density="compact"
                      bg-color="grey-lighten-4"
                      prepend-inner-icon="mdi-account"
                      clearable
                      :loading="loadingAccountHeads"
                    ></v-autocomplete>
                  </v-col>
                   <v-col lg="3" md="12" cols="12">
                    <div class="d-flex justify-start align-center flex-wrap">
                      <v-btn class="bg-primary mb-4" type="submit" @click="loadTrialBalance" elevation="2" >
                        <v-icon left>mdi-magnify</v-icon> Search
                      </v-btn>
                    </div>
                  </v-col>
                 
                </v-row>

                <!-- Export Buttons -->
                <v-row v-if="trialBalanceData" class="py-2 mt-2">
                  <v-col cols="12">
                    <div class="d-flex justify-center">
                      <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="success" @click="exportToExcel" :disabled="!trialBalanceData">EXCEL</v-btn>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="red" @click="exportToPDF" :disabled="!trialBalanceData">PDF</v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>
                </v-row>

                <!-- Trial Balance Report -->
                <v-card v-if="trialBalanceData" class="mt-2">
                  <v-card-title class="text-center">
                    <div class="w-100">
                      <div class="text-h5 font-weight-bold">Trial Balance</div>
                      <div class="text-subtitle-1">Date: {{ formattedDate(filters.from_date) }} to {{ formattedDate(filters.to_date) }}</div>
                    </div>
                  </v-card-title>

                  <v-card-text class="pa-0">
                    <!-- Trial Balance Table - Exact Image Design -->
                    <div class="trial-balance-exact">
                      <!-- Header -->
                      <div class="trial-header">
                        <div class="header-serial">S/L</div>
                        <div class="header-code">Code</div>
                        <div class="header-account" style="text-align: center;">Account Head</div>
                        <div class="header-opening">Opening Balance</div>
                        <div class="header-during">During The Period</div>
                        <div class="header-closing">Closing Balance</div>
                      </div>
                      <div class="trial-subheader">
                        <div class="header-serial"></div>
                        <div class="header-code"></div>
                        <div class="header-account"></div>
                        <div class="header-opening">
                          <div class="header-debit">Debit</div>
                          <div class="header-credit">Credit</div>
                        </div>
                        <div class="header-during">
                          <div class="header-debit">Debit</div>
                          <div class="header-credit">Credit</div>
                        </div>
                        <div class="header-closing">
                          <div class="header-debit">Debit</div>
                          <div class="header-credit">Credit</div>
                        </div>
                      </div>

                      <!-- Data Rows -->
                      <template v-if="trialBalanceData.data">
                        <div v-for="(item, index) in trialBalanceData.data" :key="'trial-' + index" class="trial-row">
                          <div class="trial-serial">{{ index + 1 }}</div>
                          <div class="trial-code">{{ item.code }}</div>
                          <div class="trial-account">{{ item.name }}</div>
                          <div class="trial-opening">
                            <div class="trial-debit">{{ formatAmount(item.opening_debit) }}</div>
                            <div class="trial-credit">{{ formatAmount(item.opening_credit) }}</div>
                          </div>
                          <div class="trial-during">
                            <div class="trial-debit">{{ formatAmount(item.during_debit) }}</div>
                            <div class="trial-credit">{{ formatAmount(item.during_credit) }}</div>
                          </div>
                          <div class="trial-closing">
                            <div class="trial-debit">{{ formatAmount(item.closing_debit) }}</div>
                            <div class="trial-credit">{{ formatAmount(item.closing_credit) }}</div>
                          </div>
                        </div>
                      </template>

                      <!-- Total Balance Row -->
                      <div class="trial-row total-row" v-if="trialBalanceData.totals">
                        <div class="trial-serial"></div>
                        <div class="trial-code"></div>
                        <div class="trial-account total-text">Total Balance</div>
                        <div class="trial-opening">
                          <div class="trial-debit total-box">{{ formatAmount(trialBalanceData.totals.opening_debit_sum) }}</div>
                          <div class="trial-credit total-box">{{ formatAmount(trialBalanceData.totals.opening_credit_sum) }}</div>
                        </div>
                        <div class="trial-during">
                          <div class="trial-debit total-box">{{ formatAmount(trialBalanceData.totals.during_debit_sum) }}</div>
                          <div class="trial-credit total-box">{{ formatAmount(trialBalanceData.totals.during_credit_sum) }}</div>
                        </div>
                        <div class="trial-closing">
                          <div class="trial-debit total-box">{{ formatAmount(trialBalanceData.totals.closing_debit_sum) }}</div>
                          <div class="trial-credit total-box">{{ formatAmount(trialBalanceData.totals.closing_credit_sum) }}</div>
                        </div>
                      </div>
                    </div>

                  </v-card-text>
                </v-card>

                <!-- Loading State -->
                <v-card v-if="loading" class="mt-4">
                  <v-card-text class="text-center py-8">
                    <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                    <p class="mt-4 text-h6">Generating Trial Balance...</p>
                  </v-card-text>
                </v-card>

                <!-- Error State -->
                <v-card v-if="error" class="mt-4">
                  <v-card-text class="text-center py-8">
                    <v-icon color="error" size="64">mdi-alert-circle</v-icon>
                    <p class="mt-4 text-h6 text-error">{{ error }}</p>
                    <v-btn color="primary" @click="loadTrialBalance" class="mt-2">
                      Try Again
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import formattedDate from '@/plugins/dateFormatter'
import { ref, onMounted, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'

const store = useStore()
const instance = getCurrentInstance()
const appContext = instance?.appContext

const panel = ref([0])
const loading = ref(false)
const loadingAccountHeads = ref(false)
const error = ref(null)
const trialBalanceData = ref(null)
const accountHeadOptions = ref([])

const filters = ref({
  from_date: null,
  to_date: null,
  acc_account_head_id: null,
})

const loadAccountHeads = async () => {
  loadingAccountHeads.value = true
  try {
    console.log('Loading all account heads...')
    await store.dispatch('getAllAccountHeadList')
    const accountHeads = store.getters.getAccountHeadList || []
    console.log('All account heads loaded:', accountHeads)
    
    accountHeadOptions.value = accountHeads.map(head => ({
      text: `${head.name} (${head.code})`,
      value: head.id
    }))
    console.log('Account head options:', accountHeadOptions.value)
  } catch (err) {
    console.error('Error loading account heads:', err)
  } finally {
    loadingAccountHeads.value = false
  }
}

const loadTrialBalance = async () => {
  loading.value = true
  error.value = null
  
  try {
    const params = {
      from_date: formattedDate(filters.value.from_date),
      to_date: formattedDate(filters.value.to_date),
      acc_account_head_id: filters.value.acc_account_head_id,
    }
    
    const response = await store.dispatch('getTrialBalance', params)
    
    if (response.success) {
      trialBalanceData.value = response
    } else {
      error.value = response.message || 'Failed to load trial balance'
    }
  } catch (err) {
    console.error('Error loading trial balance:', err)
    error.value = err.response?.data?.message || 'Failed to load trial balance'
  } finally {
    loading.value = false
  }
}

const resetFilters = () => {
  filters.value = {
    from_date: null,
    to_date: null,
    acc_account_head_id: null,
  }
  trialBalanceData.value = null
  error.value = null
}

const formatAmount = (amount) => {
  if (!amount && amount !== 0) return '0.00'
  
  // Convert to string if it's a number
  const amountStr = typeof amount === 'string' ? amount : String(amount)
  
  // Extract numeric value from strings like "1,500,000.00 Dr" or "800,000.00 Cr"
  const numericValue = parseFloat(amountStr.replace(/[^\d.-]/g, ''))
  
  if (isNaN(numericValue)) return '0.00'
  
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numericValue)
}

const exportToExcel = () => {
  if (!trialBalanceData.value) return
  
  const data = [
    ['Trial Balance'],
    [`Date: ${formattedDate(filters.value.from_date)} to ${formattedDate(filters.value.to_date)}`],
    [],
    ['S/L', 'Code', 'Account Head', 'Opening Balance', 'During The Period', 'Closing Balance'],
    ['', '', '', 'Debit', 'Credit', 'Debit', 'Credit', 'Debit', 'Credit'],
    []
  ]

  // Add data rows
  if (trialBalanceData.value.data) {
    trialBalanceData.value.data.forEach((item, index) => {
      data.push([
        index + 1,
        item.code,
        item.name,
        item.opening_debit || '0.00',
        item.opening_credit || '0.00',
        item.during_debit || '0.00',
        item.during_credit || '0.00',
        item.closing_debit || '0.00',
        item.closing_credit || '0.00'
      ])
    })
  }
  
  data.push([])
  
  // Add totals
  if (trialBalanceData.value.totals) {
    data.push([
      '',
      '',
      'Total Balance',
      trialBalanceData.value.totals.opening_debit_sum || '0.00',
      trialBalanceData.value.totals.opening_credit_sum || '0.00',
      trialBalanceData.value.totals.during_debit_sum || '0.00',
      trialBalanceData.value.totals.during_credit_sum || '0.00',
      trialBalanceData.value.totals.closing_debit_sum || '0.00',
      trialBalanceData.value.totals.closing_credit_sum || '0.00'
    ])
  }

  const proxy = appContext?.config?.globalProperties
  if (proxy?.$exportTableToExcel && proxy?.$fileDate) {
    proxy.$exportTableToExcel(data, 'trial_balance_' + proxy.$fileDate())
  }
}

const exportToPDF = () => {
  if (!trialBalanceData.value) return
  
  const columns = ['S/L', 'Code', 'Account Head', 'Opening Debit', 'Opening Credit', 'During Debit', 'During Credit', 'Closing Debit', 'Closing Credit']
  const rows = []

  // Add data rows
  if (trialBalanceData.value.data) {
    trialBalanceData.value.data.forEach((item, index) => {
      rows.push([
        index + 1,
        item.code,
        item.name,
        item.opening_debit || '0.00',
        item.opening_credit || '0.00',
        item.during_debit || '0.00',
        item.during_credit || '0.00',
        item.closing_debit || '0.00',
        item.closing_credit || '0.00'
      ])
    })
  }
  
  // Add totals
  if (trialBalanceData.value.totals) {
    rows.push([
      '',
      '',
      'Total Balance',
      trialBalanceData.value.totals.opening_debit_sum || '0.00',
      trialBalanceData.value.totals.opening_credit_sum || '0.00',
      trialBalanceData.value.totals.during_debit_sum || '0.00',
      trialBalanceData.value.totals.during_credit_sum || '0.00',
      trialBalanceData.value.totals.closing_debit_sum || '0.00',
      trialBalanceData.value.totals.closing_credit_sum || '0.00'
    ])
  }

  const proxy = appContext?.config?.globalProperties
  if (proxy?.$exportTableToPDF && proxy?.$fileDate) {
    proxy.$exportTableToPDF(
      columns,
      rows,
      `Trial Balance - ${formattedDate(filters.value.from_date)} to ${formattedDate(filters.value.to_date)}`,
      'trial_balance_' + proxy.$fileDate(),
      'landscape',
      {
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fontSize: 9, fillColor: [66, 139, 202], fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 15 },  // S/L
          1: { cellWidth: 20 },  // Code
          2: { cellWidth: 60 },  // Account Head
          3: { cellWidth: 30, halign: 'right' },  // Opening Debit
          4: { cellWidth: 30, halign: 'right' },  // Opening Credit
          5: { cellWidth: 30, halign: 'right' },  // During Debit
          6: { cellWidth: 30, halign: 'right' },  // During Credit
          7: { cellWidth: 30, halign: 'right' },  // Closing Debit
          8: { cellWidth: 30, halign: 'right' }   // Closing Credit
        },
        margin: { top: 40 }
      }
    )
  }
}

onMounted(async () => {
  // Set default dates (current year)
  const today = new Date()
  const yearStart = new Date(today.getFullYear(), 0, 1)
  
  filters.value.from_date = yearStart.toISOString().split('T')[0]
  filters.value.to_date = today.toISOString().split('T')[0]
  
  // Load account heads for filter
  await loadAccountHeads()
})
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}

/* Exact Image Design - Trial Balance Table */
.trial-balance-exact {
  font-family: 'Roboto', sans-serif;
  width: 100%;
  background-color: white;
  border: 2px solid #333;
  border-radius: 4px;
  overflow: hidden;
}

/* Header Styles */
.trial-header {
  display: flex;
  align-items: center;
  font-weight: bold;

  background-color: #e3f2fd;
  border-bottom: 2px solid #1976d2;
  color: #1976d2;
}

.trial-subheader {
  display: flex;
  align-items: center;
  font-weight: bold;

  
  background-color: #f5f5f5;
  border-bottom: 1px solid #ccc;
  color: #333;
}

.header-serial {
  flex: 0 0 50px;
 
  font-size: 14px;
  text-align: center;
  border-right: 1px solid #ddd;
}

.header-code {
  flex: 0 0 80px;
  
  font-size: 14px;
  text-align: center;
  border-right: 1px solid #ddd;
}

.header-account {
  flex: 1;
  padding: 8px 12px;
  font-size: 14px;
  text-align: left;
  border-right: 1px solid #ddd;
}

.header-opening,
.header-during,
.header-closing {
  flex: 0 0 200px;
  padding: 8px 12px;
  font-size: 14px;
  text-align: center;
  border-right: 1px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-debit,
.header-credit {
  flex: 1;
  padding: 4px 8px;
  font-size: 12px;
  text-align: center;
  border-right: 1px solid #ddd;
}

.header-credit {
  border-right: none;
}

/* Row Styles */
.trial-row {
  display: flex;
  align-items: center;
  min-height: 20px;
  border-bottom: 1px solid #e0e0e0;
  transition: background-color 0.2s ease;
}

.trial-row:hover {
  background-color: #f8f9fa;
}

.trial-row:nth-child(even) {
  background-color: #fafafa;
}

.trial-row:nth-child(even):hover {
  background-color: #f0f0f0;
}

.trial-serial {
  flex: 0 0 50px;
  padding: 8px 12px;
  font-size: 14px;
  text-align: center;
  border-right: 1px solid #ddd;
}

.trial-code {
  flex: 0 0 80px;
  padding: 8px 12px;
  font-size: 14px;
  text-align: center;
  border-right: 1px solid #ddd;
}

.trial-account {
  flex: 1;
  padding: 8px 12px;
  font-size: 14px;
  text-align: left;
  border-right: 1px solid #ddd;
}

.trial-opening,
.trial-during,
.trial-closing {
  flex: 0 0 200px;

  font-size: 14px;
  text-align: center;
  border-right: 1px solid #ddd;
  display: flex;
}

.trial-debit,
.trial-credit {
  flex: 1;
  padding: 8px 8px;
  font-size: 13px;
  text-align: right;
  border-right: 1px solid #ddd;
}

.trial-credit {
  border-right: none;
}

/* Special Row Styles */
.total-row {
  border-top: 2px solid #000;
  background-color: #f0f0f0;
  font-weight: bold;
}

.total-text {
  font-weight: bold;
}

.total-box {
  font-weight: bold;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .trial-balance-exact {
    font-size: 12px;
  }
  
  .header-opening,
  .header-during,
  .header-closing,
  .trial-opening,
  .trial-during,
  .trial-closing {
    flex: 0 0 150px;
  }
  
  .header-debit,
  .header-credit,
  .trial-debit,
  .trial-credit {
    font-size: 11px;
    padding: 2px 4px;
  }
}

@media (max-width: 768px) {
  .trial-balance-exact {
    font-size: 10px;
    overflow-x: auto;
  }
  
  .header-serial,
  .trial-serial {
    flex: 0 0 40px;
  }
  
  .header-code,
  .trial-code {
    flex: 0 0 60px;
  }
  
  .header-opening,
  .header-during,
  .header-closing,
  .trial-opening,
  .trial-during,
  .trial-closing {
    flex: 0 0 120px;
  }
  
  .header-debit,
  .header-credit,
  .trial-debit,
  .trial-credit {
    font-size: 10px;
    padding: 1px 2px;
  }
}

/* Print Styles */
@media print {
  .trial-balance-exact {
    font-size: 10px;
  }
  
  .header-opening,
  .header-during,
  .header-closing,
  .trial-opening,
  .trial-during,
  .trial-closing {
    flex: 0 0 180px;
  }
  
  .header-debit,
  .header-credit,
  .trial-debit,
  .trial-credit {
    font-size: 9px;
    padding: 2px 4px;
  }
}

/* Ensure exact alignment like image */
.trial-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.trial-account {
  flex: 1;
  min-width: 0;
}

/* Hover Effects */
.trial-row:hover .trial-serial,
.trial-row:hover .trial-code,
.trial-row:hover .trial-account,
.trial-row:hover .trial-debit,
.trial-row:hover .trial-credit {
  background-color: #f8f9fa;
}
</style>
